import { DownloadProgress } from '../contexts/DownloadProgressContext';

export class MockProgressService {
  private static instance: MockProgressService;
  private progressCallbacks: Map<string, (progress: Partial<DownloadProgress>) => void> = new Map();
  private intervals: Map<string, NodeJS.Timeout> = new Map();

  static getInstance(): MockProgressService {
    if (!MockProgressService.instance) {
      MockProgressService.instance = new MockProgressService();
    }
    return MockProgressService.instance;
  }

  // Simulate weld parameter download progress
  startMockDownload(
    downloadId: string,
    deviceId: string,
    deviceName: string,
    onProgress: (progress: Partial<DownloadProgress>) => void
  ): void {
    this.progressCallbacks.set(downloadId, onProgress);

    // Simulate file count discovery (1-5 files)
    const totalFiles = Math.floor(Math.random() * 5) + 1;
    const fileNames = [
      'weld_parameters.json',
      'machine_config.xml',
      'calibration_data.csv',
      'safety_settings.yaml',
      'operational_logs.txt'
    ];

    // Initial update with file count
    onProgress({
      totalFiles,
      status: 'in_progress',
      progress: 0,
      downloadedFiles: 0
    });

    let currentFileIndex = 0;
    let currentProgress = 0;

    const simulateProgress = () => {
      if (currentFileIndex >= totalFiles) {
        // All files downloaded
        onProgress({
          status: 'completed',
          progress: 100,
          downloadedFiles: totalFiles,
          estimatedCompletion: new Date()
        });
        this.stopMockDownload(downloadId);
        return;
      }

      // Simulate file download progress
      const fileProgress = Math.min(currentProgress, 100);
      const overallProgress = Math.floor((currentFileIndex * 100 + fileProgress) / totalFiles);

      onProgress({
        progress: overallProgress,
        downloadedFiles: currentFileIndex,
        currentFile: fileNames[currentFileIndex],
        estimatedCompletion: new Date(Date.now() + (totalFiles - currentFileIndex) * 2000)
      });

      currentProgress += Math.random() * 20 + 10; // Random progress increment

      if (currentProgress >= 100) {
        currentFileIndex++;
        currentProgress = 0;
      }
    };

    // Start progress simulation
    const interval = setInterval(simulateProgress, 500 + Math.random() * 1000);
    this.intervals.set(downloadId, interval);
  }

  // Simulate download failure
  simulateFailure(downloadId: string, error: string = 'Network timeout'): void {
    const callback = this.progressCallbacks.get(downloadId);
    if (callback) {
      callback({
        status: 'failed',
        error
      });
    }
    this.stopMockDownload(downloadId);
  }

  // Stop mock download
  stopMockDownload(downloadId: string): void {
    const interval = this.intervals.get(downloadId);
    if (interval) {
      clearInterval(interval);
      this.intervals.delete(downloadId);
    }
    this.progressCallbacks.delete(downloadId);
  }

  // Clean up all mock downloads
  cleanup(): void {
    this.intervals.forEach(interval => clearInterval(interval));
    this.intervals.clear();
    this.progressCallbacks.clear();
  }
}

export const mockProgressService = MockProgressService.getInstance();
